import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Draggable from "react-draggable";
import "./FloatingAlarmTab.css";

import Assembly from "./sound/assembly.mp3";
import NSMQ from "./sound/NSMQ.mp3";
import Break from "./sound/break.mp3";
import Breakover from "./sound/over.mp3";
import Change from "./sound/change.mp3";
import Change2 from "./sound/change2.mp3";
import Brahms from "./sound/brahmsx27-lullaby-160672.mp3";
import Calm from "./sound/calm-and-peaceful-115481.mp3";
import Closing from "./sound/closing.mp3";
import Lunch from "./sound/lunch.mp3";
import Twinkle from "./sound/twinkle-like-a-star-8026.mp3";
import Madieval from "./sound/medieval-star-188280.mp3";
import Lunchover from "./sound/lunchover.mp3";
import Storybook from "./sound/lullaby-storybook-2-317191.mp3";
import Sleep from "./sound/lullaby-sleep-piano-music-285599.mp3";
import Fairies from "./sound/lullaby-fairies-315868.mp3";
import Baby from "./sound/lullaby-baby-sleep-dreams-celestial-music-297507.mp3";
import Legacy from "./sound/legacy-of-brahms-lullaby-background-orchestral-music-for-video-full-274550.mp3";
import Deck from "./sound/deck-the-halls-background-christmas-music-for-video-calesta-version-253224.mp3";
import silencehour from "./sound/silencehour.mp3";
import startlesson from "./sound/startlesson.mp3";
import remedial from "./sound/remedial.mp3";


const Alarmlist = [
  { type: "assembly", alarm: Assembly },
  { type: "nsmq", alarm: NSMQ },
  { type: "break", alarm: Break },
  { type: "breakover", alarm: Breakover },
  { type: "change", alarm: Change },
  { type: "change2", alarm: Change2 },
  { type: "brahms", alarm: Brahms },
  { type: "calm", alarm: Calm },
  { type: "closing", alarm: Closing },
  { type: "lunch", alarm: Lunch },
  { type: "twinkle", alarm: Twinkle },
  { type: "madieval", alarm: Madieval },
  { type: "lunchover", alarm: Lunchover },
  { type: "storybook", alarm: Storybook },
  { type: "sleep", alarm: Sleep },
  { type: "fairies", alarm: Fairies },
  { type: "baby", alarm: Baby },
  { type: "legacy", alarm: Legacy },
  { type: "deck", alarm: Deck },
  { type: "silencehour", alarm: silencehour },
  { type: "startlesson", alarm: startlesson },
  { type: "remedial", alarm: remedial },
];

const AlarmClock = () => {
  const schooldetails_ = useSelector((state) => state.schooldetails);
  const alarms = schooldetails_[0]?.siren || [];

  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextAlarm, setNextAlarm] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [isMinimized, setIsMinimized] = useState(true);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isCountdownActive, setIsCountdownActive] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(false);

  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const draggableRef = useRef(null);

  const enableSound = () => {
    audioContextRef.current =
      audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current.resume();
    setSoundEnabled(true);
  };

  // Parse 24-hour or AM/PM time to Date
  const parseTimeToDate = (timeStr, baseDate) => {
    let hours, minutes;

    if (timeStr.includes(" ")) {
      const [time, period] = timeStr.split(" ");
      [hours, minutes] = time.split(":").map(Number);
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
    } else {
      [hours, minutes] = timeStr.split(":").map(Number);
    }

    const date = new Date(baseDate);
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const findNextAlarm = (alarms) => {
    const today = currentTime.toLocaleString("en-US", { weekday: "long" });
    const now = currentTime.getTime();
    const todayAlarms = alarms.filter((a) => a.isActive && a.days.includes(today));

    const future = todayAlarms
      .map((a) => ({ ...a, alarmTime: parseTimeToDate(a.time, currentTime).getTime() }))
      .filter((a) => a.alarmTime > now)
      .sort((a, b) => a.alarmTime - b.alarmTime);

    return future[0] || null;
  };

  // Play alarm: first sound 30s, then second sound
  const playAlarmSound = () => {
    if(!soundEnabled){
      enableSound();
    }
    if (!nextAlarm || hasTriggered || !soundEnabled || !nextAlarm.isActive) return;
  
    
    setHasTriggered(true);
    setIsCountdownActive(false);

    const firstSrc = Alarmlist.find((x) => x.type === nextAlarm.sound)?.alarm || Deck;
    const secondSrc = Alarmlist.find((x) => x.type === nextAlarm.type)?.alarm || Deck;

    const first = new Audio(firstSrc);
    const second = new Audio(secondSrc);

    audioRef.current = first;

    first.oncanplaythrough = () => {
      first.play();

      // Stop first audio after 30 seconds
      setTimeout(() => {
        first.pause();
        first.src = "";
        first.load();

        audioRef.current = second;
        second.play();

        second.onended = () => {
          setHasTriggered(false);
          setIsCountdownActive(true);
        };
      }, 30000); // 30 seconds
    };

    first.onerror = () => {
      console.error("First audio failed");
      setHasTriggered(false);
      setIsCountdownActive(true);
    };
  };

  useEffect(() => {
    if (!isCountdownActive) return;

    const interval = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);

      const next = findNextAlarm(alarms);
      setNextAlarm(next);

      if (!next) {
        setCountdown("No alarms today");
        return;
      }

      const diff = next.alarmTime - now.getTime();

      if (diff <= 1000 && !hasTriggered) {
        playAlarmSound();
        setCountdown("Alarm triggered!");
      } else {
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        setCountdown(`${h}h ${m}m ${s}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [alarms, isCountdownActive, hasTriggered, soundEnabled]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Reset draggable ref when nextAlarm changes
  useEffect(() => {
    if (!nextAlarm) {
      draggableRef.current = null;
    }
  }, [nextAlarm]);

  return (
    <>
      {nextAlarm && (
        <Draggable handle=".panel-header" nodeRef={draggableRef}>
          <div
            ref={draggableRef}
            className={`floating-panel ${isMinimized ? "minimized" : ""}`}
            style={{ backgroundImage: "linear-gradient(315deg, #17dddd 0%, #1760d3 74%)" }}
          >
            <div className="panel-header">
              <div className="panel-title">
                Next Siren ({nextAlarm.name}: {countdown})
              </div>
              <button onClick={() => setIsMinimized(!isMinimized)}>
                {isMinimized ? "+" : "−"}
              </button>
            </div>

            <div className="panel-content">
              <h3>{nextAlarm.name}</h3>
              <p>Current: {currentTime.toLocaleTimeString()}</p>
              <p>Alarm: {nextAlarm.time}</p>
              <p>Status: {nextAlarm.isActive ? "Active" : "Inactive"}</p>
            </div>
          </div>
        </Draggable>
      )}

      {/* {!soundEnabled && (
        <button
          onClick={enableSound}
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            padding: "10px 20px",
            zIndex: 9999,
          }}
        >
          🔊 Enable Alarm Sound
        </button>
      )} */}
    </>
  );
};

export default AlarmClock;
